import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Photo } from '@/models/Photo';
import { LayoutRectangle } from 'react-native';
import { usePathname } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { PhotosService } from '~/services/domain/PhotosService';

interface IImageViewerOptions {
  allowImageDownload?: boolean;
  photoId?: number | string;
  /** Hide the photo's social actions (likes/comments/caption/context menu), keeping the owner info chip, for non-Photo images. */
  disableSocialActions?: boolean;
}

interface ImageViewerActionsContextType {
  showImage: (
    uri: string,
    photo?: Photo,
    thumbnailLayout?: LayoutRectangle,
    photos?: Photo[],
    options?: IImageViewerOptions
  ) => void;
  updatePhoto: (photo: Photo | null) => void;
  setLoadError: (error: unknown) => void;
  hideImage: () => void;
}

interface ImageViewerStateContextType {
  isVisible: boolean;
  isClosingDueToNavigation: boolean;
  currentUri: string | null;
  currentPhoto: Photo | null;
  thumbnailLayout: LayoutRectangle | null;
  currentPhotos: Photo[] | null;
  loadError: unknown;
  allowImageDownload: boolean;
  disableSocialActions: boolean;
}

export type ImageViewerContextType = ImageViewerActionsContextType & ImageViewerStateContextType;

const ImageViewerActionsContext = createContext<ImageViewerActionsContextType | null>(null);
const ImageViewerStateContext = createContext<ImageViewerStateContextType | null>(null);

interface ImageViewerProviderProps {
  children: ReactNode;
}

export const ImageViewerProvider: React.FC<ImageViewerProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosingDueToNavigation, setIsClosingDueToNavigation] = useState(false);
  const [currentUri, setCurrentUri] = useState<string | null>(null);
  const [currentPhoto, setCurrentPhoto] = useState<Photo | null>(null);
  const [thumbnailLayout, setThumbnailLayout] = useState<LayoutRectangle | null>(null);
  const [currentPhotos, setCurrentPhotos] = useState<Photo[] | null>(null);
  const [loadError, setLoadErrorState] = useState<unknown>(null);
  const [allowImageDownload, setAllowImageDownload] = useState(false);
  const [disableSocialActions, setDisableSocialActions] = useState(false);
  const pathname = usePathname();
  const lastPathnameRef = useRef<string | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openGenerationRef = useRef(0);
  const queryClient = useQueryClient();

  const updatePhoto = useCallback((photo: Photo | null) => {
    setCurrentPhoto(photo);
    if (photo) setLoadErrorState(null);
  }, []);

  const setLoadError = useCallback((error: unknown) => {
    setLoadErrorState(error ?? null);
  }, []);

  const hideImage = useCallback(() => {
    openGenerationRef.current += 1;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(false);
    setIsClosingDueToNavigation(false);
    hideTimeoutRef.current = setTimeout(() => {
      setCurrentUri(null);
      setCurrentPhoto(null);
      setThumbnailLayout(null);
      setCurrentPhotos(null);
      setLoadErrorState(null);
      setAllowImageDownload(false);
      setDisableSocialActions(false);
      hideTimeoutRef.current = null;
    }, 300);
  }, []);

  const showImage = useCallback((
    uri: string,
    photo?: Photo,
    thumbLayout?: LayoutRectangle,
    photos?: Photo[],
    options?: IImageViewerOptions
  ) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const generation = ++openGenerationRef.current;

    setIsClosingDueToNavigation(false);
    setCurrentUri(uri);
    setCurrentPhoto(photo || null);
    setThumbnailLayout(thumbLayout || null);
    setCurrentPhotos(photos ?? null);
    setLoadErrorState(null);
    setAllowImageDownload(options?.allowImageDownload === true);
    setDisableSocialActions(options?.disableSocialActions === true);
    setIsVisible(true);

    const photoId = options?.photoId;
    if (photoId && !photo) {
      (async () => {
        try {
          const details = await queryClient.fetchQuery({
            queryKey: ['photo', photoId],
            queryFn: () => PhotosService.getPhotoDetails(photoId),
          });
          if (openGenerationRef.current === generation) {
            updatePhoto(details);
          }
        } catch (err) {
          if (openGenerationRef.current === generation) {
            setLoadError(err);
          }
        }
      })();
    }
  }, [queryClient, updatePhoto, setLoadError]);

  useEffect(() => {
    if (lastPathnameRef.current === null) {
      lastPathnameRef.current = pathname ?? null;
      return;
    }

    if (pathname !== lastPathnameRef.current) {
      lastPathnameRef.current = pathname ?? null;
      if (isVisible) {
        setIsClosingDueToNavigation(true);
        setTimeout(() => {
          hideImage();
        }, 50);
      }
    }
  }, [pathname, isVisible, hideImage]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const actionsValue = useMemo<ImageViewerActionsContextType>(
    () => ({
      showImage,
      updatePhoto,
      setLoadError,
      hideImage,
    }),
    [showImage, updatePhoto, setLoadError, hideImage]
  );

  const stateValue = useMemo<ImageViewerStateContextType>(
    () => ({
      isVisible,
      isClosingDueToNavigation,
      currentUri,
      currentPhoto,
      thumbnailLayout,
      currentPhotos,
      loadError,
      allowImageDownload,
      disableSocialActions,
    }),
    [
      isVisible,
      isClosingDueToNavigation,
      currentUri,
      currentPhoto,
      thumbnailLayout,
      currentPhotos,
      loadError,
      allowImageDownload,
      disableSocialActions,
    ]
  );

  return (
    <ImageViewerActionsContext.Provider value={actionsValue}>
      <ImageViewerStateContext.Provider value={stateValue}>
        {children}
      </ImageViewerStateContext.Provider>
    </ImageViewerActionsContext.Provider>
  );
};

export const useImageViewerActions = (): ImageViewerActionsContextType => {
  const context = useContext(ImageViewerActionsContext);
  if (!context) {
    throw new Error('useImageViewerActions must be used within an ImageViewerProvider');
  }
  return context;
};

export const useImageViewerState = (): ImageViewerStateContextType => {
  const context = useContext(ImageViewerStateContext);
  if (!context) {
    throw new Error('useImageViewerState must be used within an ImageViewerProvider');
  }
  return context;
};

export const useImageViewer = (): ImageViewerContextType => {
  const actions = useImageViewerActions();
  const state = useImageViewerState();
  return { ...actions, ...state };
};
